import { createContext, FC, useState, useEffect, useContext, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { validateEmail as emailFormatIsValid } from '../util/validation/formValidation';
import { ERoutes, maxEmailLength, maxStaffFirstNameLength, maxStaffLastNameLength } from '../util/constants';
import GlobalContext from './GlobalContext';
import { TMigrateStaffAccountPost, TLoginPost } from '../api/types';
import API from '../api/api';
import { validatePassword } from '../util/validation/passwordValidation';
import { replacePhrase } from '../util/helpers';

type TMigrateStaffAccountContext = {
    migrateStaffAccountUiState: EMigrateStaffAccountUiState;
    setMigrateStaffAccountUiState: (state: EMigrateStaffAccountUiState) => void;
    firstName: string;
    setFirstName: (name: string) => void;
    firstNameIsValid: boolean;
    firstNameValidationMessage: string;
    firstNameValidationIsActive: boolean;
    setFirstNameValidationIsActive: (isActive: boolean) => void;
    lastName: string;
    setLastName: (name: string) => void;
    lastNameIsValid: boolean;
    lastNameValidationMessage: string;
    lastNameValidationIsActive: boolean;
    setLastNameValidationIsActive: (isActive: boolean) => void;
    email: string;
    setEmail: (email: string) => void;
    emailIsValid: boolean;
    emailValidationMessage: string;
    emailValidationIsActive: boolean;
    setEmailValidationIsActive: (isActive: boolean) => void;
    startedWithValidEmail: boolean;
    startedWithValidNames: boolean;
    password: string;
    passwordIsValid: boolean;
    setPassword: (password: string) => void;
    setPasswordHasFocus: (hasFocus: boolean) => void;
    passwordHasValidated: boolean;
    setPasswordHasValidated: (hasValidated: boolean) => void;
    passwordValidationIsActive: boolean;
    password2: string;
    setPassword2: (password2: string) => void;
    password2IsValid: boolean;
    password2ValidationMessage: string;
    setPassword2HasValidated: (hasValidated: boolean) => void;
    setPassword2HasFocus: (hasFocus: boolean) => void;
    disablePasswordSubmit: boolean;
    disableInfoSubmit: boolean;
    submitInfoForm: () => void;
    submitMigrateAccount: () => Promise<void>;
    login: () => Promise<void>;
    redirectUrl: string;
};

export type TMigrateStaffAccountRouterState = {
    migrationUser: {
        firstName: string;
        lastName: string;
        email: string;
        linkingToken: string;
    };
};

export enum EMigrateStaffAccountUiState {
    default,
    ready,
    loading,
    password,
    verified,
    verificationRequired,
    userExists,
    login,
    softError,
    fatalError,
}

const MigrateStaffAccountContext = createContext<TMigrateStaffAccountContext | undefined>(undefined);

export const MigrateStaffAccountProvider: FC = ({ children }) => {
    const {
        language,
        gatewayAPIBaseUrl,
        returnUrlParameter,
        subscriberId,
        customerAccountId,
        putFullLoginLinkInLocalStorage,
        csrfToken,
    } = useContext(GlobalContext);
    const [migrateStaffAccountUiState, setMigrateStaffAccountUiState] = useState(EMigrateStaffAccountUiState.default);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [startedWithValidEmail, setStartedWithValidEmail] = useState(false);
    const [startedWithValidNames, setStartedWithValidNames] = useState(false);
    const [linkingToken, setLinkingToken] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const { state: routerState }: { state: TMigrateStaffAccountRouterState } = useLocation();
    const history = useHistory();

    // ------
    // Setup
    // ------
    const parseRouterState = () => {
        if (routerState) {
            const { firstName, lastName, email, linkingToken } = routerState.migrationUser;
            const firstNameIsValid = firstName && firstName.length <= maxStaffFirstNameLength ? true : false;
            const lastNameIsValid = lastName && lastName.length <= maxStaffLastNameLength ? true : false;
            if (email && email.length <= maxEmailLength && emailFormatIsValid(email)) {
                setEmail(email);
                setStartedWithValidEmail(true);
                setEmailValidationIsActive(true);
            }
            if (firstNameIsValid) {
                setFirstName(firstName);
                setFirstNameValidationIsActive(true);
            }
            if (lastNameIsValid) {
                setLastName(lastName);
                setLastNameValidationIsActive(true);
            }
            if (firstNameIsValid && lastNameIsValid) {
                setStartedWithValidNames(true);
            }
            setLinkingToken(linkingToken);
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.ready);
        } else {
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.fatalError);
        }
    };

    useEffect(() => {
        switch (migrateStaffAccountUiState) {
            case EMigrateStaffAccountUiState.default:
                parseRouterState();
                break;
            case EMigrateStaffAccountUiState.userExists:
                setEmailIsValid(false);
                setEmailValidationIsActive(true);
                setEmailValidationMessage(language.staffAccountMigration.email.userExists);
                setPassword('');
                setPassword2('');
                setPasswordIsValid(true);
                setPassword2IsValid(true);
                setPasswordHasValidated(false);
                setPassword2HasValidated(false);
                setPasswordValidationIsActive(false);
                break;
            case EMigrateStaffAccountUiState.verificationRequired:
                // local storage used to retrieve login link after verification
                putFullLoginLinkInLocalStorage();
                break;
            default:
                break;
        }
    }, [migrateStaffAccountUiState]);

    // ---------
    // Info form
    // ---------
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [emailValidationMessage, setEmailValidationMessage] = useState('');
    const [emailValidationIsActive, setEmailValidationIsActive] = useState(false);
    const [firstNameIsValid, setFirstNameIsValid] = useState(false);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [firstNameValidationIsActive, setFirstNameValidationIsActive] = useState(false);
    const [lastNameIsValid, setLastNameIsValid] = useState(false);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [lastNameValidationIsActive, setLastNameValidationIsActive] = useState(false);
    const [disableInfoSubmit, setDisableInfoSubmit] = useState(true);
    const formattedFirstNameMaxLength = useMemo(() => {
        const desc1 = replacePhrase(language.validation.maxLength, '<noun>', language.common.firstName);
        const desc2 = replacePhrase(desc1, '<length>', maxStaffFirstNameLength.toString());
        return desc2;
    }, [language, maxStaffFirstNameLength]);
    const formattedLastNameMaxLength = useMemo(() => {
        const desc1 = replacePhrase(language.validation.maxLength, '<noun>', language.common.lastName);
        const desc2 = replacePhrase(desc1, '<length>', maxStaffLastNameLength.toString());
        return desc2;
    }, [language, maxStaffLastNameLength]);

    const checkEmailIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!email.length) {
            validationMessage = language.validation.emailMissing;
            isValid = false;
        } else if (email.length > maxEmailLength) {
            isValid = false;
            validationMessage = language.validation.emailMaxLength;
        } else if (!emailFormatIsValid(email)) {
            isValid = false;
            validationMessage = language.validation.email;
        }
        setEmailIsValid(isValid);
        setEmailValidationMessage(validationMessage);
    };

    const checkFirstNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!firstName.length) {
            validationMessage = language.validation.firstName;
            isValid = false;
        } else if (firstName.length > maxStaffFirstNameLength) {
            validationMessage = formattedFirstNameMaxLength;
            isValid = false;
        }
        setFirstNameIsValid(isValid);
        setFirstNameValidationMessage(validationMessage);
    };

    const checkLastNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!lastName.length) {
            validationMessage = language.validation.lastName;
            isValid = false;
        } else if (lastName.length > maxStaffLastNameLength) {
            validationMessage = formattedLastNameMaxLength;
            isValid = false;
        }
        setLastNameIsValid(isValid);
        setLastNameValidationMessage(validationMessage);
    };

    const checkDisableInfoSubmit = () => {
        if (emailIsValid && firstNameIsValid && lastNameIsValid) {
            setDisableInfoSubmit(false);
        } else {
            setDisableInfoSubmit(true);
        }
    };

    const submitInfoForm = () => {
        setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.ready);
        history.push(ERoutes.staffMigrateAccountPassword);
    };

    useEffect(() => {
        checkEmailIsValid();
    }, [email]);

    useEffect(() => {
        checkFirstNameIsValid();
    }, [firstName]);

    useEffect(() => {
        checkLastNameIsValid();
    }, [lastName]);

    useEffect(() => {
        checkDisableInfoSubmit();
    }, [emailIsValid, firstNameIsValid, lastNameIsValid]);

    // ---------
    // Password
    // ---------
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [passwordHasFocus, setPasswordHasFocus] = useState(false);
    const [passwordHasValidated, setPasswordHasValidated] = useState(false);
    const [passwordValidationIsActive, setPasswordValidationIsActive] = useState(false);
    const [password2, setPassword2] = useState('');
    const [password2IsValid, setPassword2IsValid] = useState(true);
    const [password2ValidationMessage, setPassword2ValidationMessage] = useState('');
    const [password2HasValidated, setPassword2HasValidated] = useState(false);
    const [password2HasFocus, setPassword2HasFocus] = useState(false);
    const [disablePasswordSubmit, setDisablePasswordSubmit] = useState(true);

    const checkDisablePasswordSubmit = () => {
        if (password.length && passwordIsValid && password === password2) {
            setDisablePasswordSubmit(false);
        } else {
            if (disablePasswordSubmit === false) {
                setDisablePasswordSubmit(true);
            }
        }
    };

    const checkPasswordIsValid = () => {
        if (passwordHasValidated) {
            if (validatePassword(password)) {
                setPasswordIsValid(true);
            } else {
                if (passwordIsValid === true) {
                    setPasswordIsValid(false);
                }
            }
        }
    };

    const checkPassword2IsValid = () => {
        if (password2HasValidated) {
            if (validatePassword(password2) && password === password2) {
                setPassword2IsValid(true);
            } else {
                if (password2IsValid === true) {
                    setPassword2IsValid(false);
                }
                if (password2 !== password) {
                    setPassword2ValidationMessage(language.resetPassword.passwordsDontMatch);
                } else {
                    setPassword2ValidationMessage(undefined);
                }
            }
        }
    };

    useEffect(() => {
        password.length > 0 && setPasswordValidationIsActive(true);
        checkPasswordIsValid();
        checkPassword2IsValid();
        checkDisablePasswordSubmit();
    }, [password, password2, passwordHasFocus, password2HasFocus]);

    // ------------
    // Util
    // ------------
    const submitMigrateAccount = async () => {
        const postObject: TMigrateStaffAccountPost = {
            firstName,
            lastName,
            email,
            password,
            linkingToken,
        };
        try {
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.loading);
            const response = await API.post.migrateStaffAccount(gatewayAPIBaseUrl, postObject);
            const { requiresVerification } = response.data;
            if (requiresVerification) {
                setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.verificationRequired);
            } else {
                setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.verified);
            }
        } catch (error) {
            if (error.response?.status === 409) {
                setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.userExists);
            } else {
                console.error('Failed to migrate staff account');
                setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.softError);
            }
        }
    };

    const login = async () => {
        const postObject: TLoginPost = {
            username: email,
            password,
            isStaff: true,
            ...(subscriberId && { subscriberId }),
            ...(customerAccountId && { customerAccountId }),
        };
        try {
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.loading);
            const response = await API.post.login(postObject, `?${returnUrlParameter}`, csrfToken);
            setRedirectUrl(response.data.redirectUrl);
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.login);
        } catch {
            setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.fatalError);
        }
    };

    // --------
    // Context Provider
    // --------
    return (
        <MigrateStaffAccountContext.Provider
            value={{
                migrateStaffAccountUiState,
                setMigrateStaffAccountUiState,
                firstName,
                setFirstName,
                firstNameIsValid,
                firstNameValidationMessage,
                firstNameValidationIsActive,
                setFirstNameValidationIsActive,
                lastName,
                setLastName,
                lastNameIsValid,
                lastNameValidationMessage,
                lastNameValidationIsActive,
                setLastNameValidationIsActive,
                email,
                emailIsValid,
                emailValidationMessage,
                emailValidationIsActive,
                setEmailValidationIsActive,
                setEmail,
                startedWithValidEmail,
                startedWithValidNames,
                password,
                setPassword,
                passwordIsValid,
                setPasswordHasFocus,
                setPasswordHasValidated,
                passwordHasValidated,
                password2,
                setPassword2,
                password2IsValid,
                setPassword2HasFocus,
                setPassword2HasValidated,
                password2ValidationMessage,
                passwordValidationIsActive,
                disablePasswordSubmit,
                disableInfoSubmit,
                submitInfoForm,
                submitMigrateAccount,
                login,
                redirectUrl,
            }}
        >
            {children}
        </MigrateStaffAccountContext.Provider>
    );
};

export default MigrateStaffAccountContext;
